import type { SupabaseClient } from '@supabase/supabase-js';

export type CompanyLinkCore = {
    id?: string;
    name: string;
    website?: string;
    email?: string;
    phone?: string;
    roleOrTitle?: string | null;
    logo?: string | null;
};

export type CompanyRow = {
    id: string;
    contact_id: string;
    name: string;
    role_title: string | null;
    website: string | null;
    email: string | null;
    phone: string | null;
    logo_url: string | null;
    created_at?: string;
    updated_at?: string;
};

const toLink = (r: CompanyRow): CompanyLinkCore => ({
    id: r.id,
    name: r.name,
    roleOrTitle: r.role_title || undefined,
    website: r.website || undefined,
    email: r.email || undefined,
    phone: r.phone || undefined,
    logo: r.logo_url || undefined,
});

const sanitizeLogo = (logo?: string | null) => {
    if (!logo) return null;
    try { if (logo.startsWith('blob:')) return null; } catch { }
    return logo;
};

const fromLink = (contactId: string, l: CompanyLinkCore) => ({
    contact_id: contactId,
    name: l.name,
    role_title: l.roleOrTitle ?? null,
    website: l.website ?? null,
    email: l.email ?? null,
    phone: l.phone ?? null,
    logo_url: sanitizeLogo(l.logo as string | null),
});

export async function listCompaniesCore(sb: SupabaseClient, contactId: string): Promise<CompanyLinkCore[]> {
    const { data, error } = await sb
        .from('contact_companies')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });
    if (error) {
        const msg = String((error as any)?.message || (error as any)?.details || error);
        if (/relation .*contact_companies.* does not exist/i.test(msg) || /table .*contact_companies.* does not exist/i.test(msg)) {
            throw new Error('Companies feature not initialized. Apply migration 20250810_create_contact_companies.sql');
        }
        throw error;
    }
    return (data as CompanyRow[] | null)?.map(toLink) || [];
}

export async function createCompanyCore(sb: SupabaseClient, contactId: string, link: CompanyLinkCore): Promise<CompanyLinkCore> {
    const payload = fromLink(contactId, link);
    const { data, error } = await sb
        .from('contact_companies')
        .insert(payload)
        .select('*')
        .single();
    if (error) {
        const code = (error as any)?.code;
        const msg = String((error as any)?.message || (error as any)?.details || error);
        if (/relation .*contact_companies.* does not exist/i.test(msg) || /table .*contact_companies.* does not exist/i.test(msg)) {
            throw new Error('Companies feature not initialized. Apply migration 20250810_create_contact_companies.sql');
        }
        if (code === '23503' || /foreign key/i.test(msg)) {
            throw new Error('Cannot link company: contact not found or not accessible.');
        }
        throw new Error(msg);
    }
    return toLink(data as CompanyRow);
}

export async function updateCompanyCore(sb: SupabaseClient, id: string, link: CompanyLinkCore): Promise<CompanyLinkCore> {
    const { data, error } = await sb
        .from('contact_companies')
        .update({
            name: link.name,
            role_title: link.roleOrTitle ?? null,
            website: link.website ?? null,
            email: link.email ?? null,
            phone: link.phone ?? null,
            logo_url: sanitizeLogo(link.logo as string | null),
            updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*')
        .single();
    if (error) {
        const msg = String((error as any)?.message || (error as any)?.details || error);
        if (/relation .*contact_companies.* does not exist/i.test(msg)) {
            throw new Error('Companies feature not initialized. Apply migration 20250810_create_contact_companies.sql');
        }
        throw new Error(msg);
    }
    return toLink(data as CompanyRow);
}

export async function deleteCompanyCore(sb: SupabaseClient, id: string): Promise<void> {
    const { error } = await sb
        .from('contact_companies')
        .delete()
        .eq('id', id);
    if (error) {
        const msg = String((error as any)?.message || (error as any)?.details || error);
        if (/relation .*contact_companies.* does not exist/i.test(msg)) {
            throw new Error('Companies feature not initialized. Apply migration 20250810_create_contact_companies.sql');
        }
        throw new Error(msg);
    }
}
